import React, { useState } from 'react';
import {
  IAuditoriaPlIntegridadeResultado,
  IAuditoriaPlIntegridadeItem,
} from '../interfaces/IAuditoriaPlIntegridade';

interface Props {
  resultado: IAuditoriaPlIntegridadeResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'AVISO'  ? 'badge-light-info' :
    status === 'ALERTA' ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const TipoBadge: React.FC<{ tipo: string }> = ({ tipo }) => {
  if (tipo === 'CONTRAPARTIDA_CAIXA')
    return <span className='badge badge-light-danger'>Contrapartida Caixa/Banco</span>;
  if (tipo === 'CONTRAPARTIDA_CAIXA_PENDENTE_VALIDACAO')
    return <span className='badge badge-light-info'>Pendente de Validação Documental</span>;
  return <span className='badge badge-light-warning'>Dia Não Útil</span>;
};

const ItemRow: React.FC<{ item: IAuditoriaPlIntegridadeItem }> = ({ item }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr>
        <td>
          <button
            className='btn btn-sm btn-icon btn-light me-2'
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} />
          </button>
          <span className='fw-bold'>{item.empresaNome}</span>
          <br /><span className='text-muted fs-8'>{item.cnpj}</span>
        </td>
        <td className='text-center fw-bold'>{item.dataLancamento}</td>
        <td>
          <span className='fw-semibold'>{item.descricaoContaPl}</span>
          <br /><span className='text-muted fs-8'>{item.contaPl}</span>
        </td>
        <td className='text-center'>
          <span className={`badge ${item.tipoLancamento === 'C' ? 'badge-light-success' : 'badge-light-danger'}`}>
            {item.tipoLancamento === 'C' ? 'Crédito' : 'Débito'}
          </span>
        </td>
        <td className='text-end fw-bold'>{formatCurrency(item.valor)}</td>
        <td className='text-end fw-bold text-danger'>{formatCurrency(item.passivoTributario)}</td>
        <td className='text-center'><TipoBadge tipo={item.tipoOcorrencia} /></td>
        <td className='text-center'><StatusBadge status={item.status} /></td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={8} className='bg-light'>
            <div className='p-4'>
              {/* Motivo da ocorrência */}
              <div className='mb-3'>
                <span className='fw-bold text-muted fs-8'>Motivo: </span>
                <span className='fw-semibold'>{item.motivoDetalhado}</span>
                {item.contaContrapartida && (
                  <span className='ms-3 text-muted fs-8'>Conta Caixa/Banco detectada: <strong>{item.contaContrapartida}</strong></span>
                )}
              </div>

              {/* Histórico do lançamento */}
              {item.historico && (
                <div className='mb-3'>
                  <span className='fw-bold text-muted fs-8'>Histórico: </span>
                  <span className='text-muted'>{item.historico}</span>
                </div>
              )}

              {/* Passivo (apenas ERRO) */}
              {item.status === 'ERRO' && (
                <div className='row g-3 mb-3'>
                  <div className='col-md-3'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Tributo (IRPJ 25% + CSLL 9%)</div>
                      <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.tributoDevido)}</div>
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Multa Art. 44 (75%)</div>
                      <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.multaArt44)}</div>
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Juros SELIC</div>
                      <div className='fw-bolder fs-5 text-warning'>{formatCurrency(item.jurosSelic)}</div>
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Passivo Total</div>
                      <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.passivoTributario)}</div>
                    </div>
                  </div>
                </div>
              )}

              <div className={`alert p-3 fs-8 ${
                item.status === 'ERRO'  ? 'alert-light-danger border border-danger' :
                item.status === 'AVISO' ? 'alert-light-info border border-info' :
                                          'alert-light-warning border border-warning'
              }`}>
                {item.mensagemDiagnostico}
              </div>

              {item.baseLegal && (
                <div className='alert alert-light border border-secondary p-2 fs-9 mt-2'>
                  <strong>Base Legal:</strong> {item.baseLegal}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaPlIntegridadeDetalhe: React.FC<Props> = ({ resultado }) => {
  if (!resultado) return null;

  return (
    <div className='card card-custom card-stretch mb-5'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-4 mb-1'>
            Detalhamento — Integridade de Lançamentos no Patrimônio Líquido
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            {resultado.itens.length} ocorrência(s) detectada(s) ·{' '}
            {resultado.totalErros} erro(s) contrapartida Caixa/Banco · {resultado.totalAvisos} aviso(s) pendente(s) de validação · {resultado.totalAlertas} alerta(s) dia não útil
            {resultado.passivoTributarioTotal > 0 && (
              <> · Passivo estimado:{' '}
                <strong className='text-danger'>
                  {resultado.passivoTributarioTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </strong>
              </>
            )}
          </span>
        </h3>
      </div>

      {resultado.mensagemDirecionamento && (
        <div className='card-body pb-0'>
          <div className={`alert p-3 fs-8 ${
            resultado.status === 'ERRO'   ? 'alert-light-danger border border-danger' :
            resultado.status === 'AVISO'  ? 'alert-light-info border border-info' :
            resultado.status === 'ALERTA' ? 'alert-light-warning border border-warning' :
                                            'alert-light-success border border-success'
          }`}>
            {resultado.mensagemDirecionamento}
          </div>
        </div>
      )}

      <div className='card-body pt-3'>
        {resultado.itens.length === 0 ? (
          <div className='text-success fw-bold fs-5 text-center py-8'>
            ✓ Nenhum lançamento irregular no Patrimônio Líquido identificado. Situação regular.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th className='text-center'>Data</th>
                  <th>Conta PL</th>
                  <th className='text-center'>Natureza</th>
                  <th className='text-end'>Valor</th>
                  <th className='text-end'>Passivo</th>
                  <th className='text-center'>Ocorrência</th>
                  <th className='text-center'>Status</th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemRow key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {resultado.fundamentacaoLegal && (
          <div className='alert alert-light border border-secondary p-3 fs-9 mt-4'>
            <strong>Fundamentação Legal:</strong> {resultado.fundamentacaoLegal}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaPlIntegridadeDetalhe;
